<template>
  <CModal
      :show.sync="visible"
      size="lg"
      :title="$pgettext('collaborators.virtualusers', 'Export Virtual Users')"
      @hidden="onHidden"
      :close-on-backdrop="false"
  >
    <!-- Initial State: List Virtual Users -->
    <div v-if="state === 'initial'">
      <CAlert color="info" class="mb-3">
        <div class="d-flex align-items-start">
          <CIcon name="cilInfo" size="lg" class="mr-3 flex-shrink-0"/>
          <div>
            <strong>
              <translate translate-context="collaborators.virtualusers">
                Virtual User Passwords
              </translate>
            </strong>
            <p class="mb-0 mt-2">
              <translate translate-context="collaborators.virtualusers">
                Passwords are not stored in plain text and cannot be displayed. To export credentials, all passwords
                must be reset first.
              </translate>
            </p>
          </div>
        </div>
      </CAlert>

      <div v-if="virtualUsers.length === 0" class="text-center py-4">
        <CIcon name="cilUser" size="3xl" class="text-muted mb-3"/>
        <p class="text-muted">
          <translate translate-context="collaborators.virtualusers">
            No virtual users found in this scope
          </translate>
        </p>
      </div>

      <div v-else>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">
            <translate translate-context="collaborators.virtualusers" :translate-params="{count: virtualUsers.length}">
              Virtual Users (%{count})
            </translate>
          </h6>
          <CButton
              color="link"
              size="sm"
              @click="toggleSelectAll"
          >
            <translate v-if="allSelected" translate-context="collaborators.virtualusers">Deselect All</translate>
            <translate v-else translate-context="collaborators.virtualusers">Select All</translate>
          </CButton>
        </div>

        <CDataTable
            :items="virtualUsers"
            :fields="userTableFields"
            hover
            clickable-rows
            @row-clicked="handleRowClick"
            class="user-selection-table mb-3"
        >
          <template #selected="{item}">
            <td class="text-center align-middle">
              <CSwitch
                  class="mx-1"
                  color="primary"
                  variant="3d"
                  size="sm"
                  :checked="selectedUsers.includes(item.id)"
                  @update:checked="toggleUser(item.id)"
                  @click.native.stop
              />
            </td>
          </template>

          <template #email="{item}">
            <td class="align-middle">
              <strong>{{ item.email }}</strong>
            </td>
          </template>

          <template #roles="{item}">
            <td class="align-middle">
              <span class="text-muted">
                {{ item.roles.map(r => r.name).join(', ') }}
              </span>
            </td>
          </template>
        </CDataTable>

        <CAlert color="warning">
          <CIcon name="cilWarning" class="mr-2"/>
          <translate translate-context="collaborators.virtualusers" :translate-params="{count: selectedCount}">
            Clicking "Reset Passwords" will generate new passwords for %{count} selected virtual user(s).
          </translate>
        </CAlert>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="state === 'loading'" class="text-center py-5">
      <CSpinner color="primary"/>
      <p class="mt-3 text-muted">
        <translate translate-context="collaborators.virtualusers">
          Resetting passwords...
        </translate>
      </p>
    </div>


    <template #footer>
      <CButton
          v-if="state === 'initial'"
          color="secondary"
          @click="close"
      >
        <translate translate-context="collaborators.virtualusers">Cancel</translate>
      </CButton>

      <CButton
          v-if="state === 'initial' && virtualUsers.length > 0"
          color="primary"
          @click="resetPasswords"
          :disabled="selectedUsers.length === 0"
      >
        <CIcon name="cilLockLocked" class="mr-2"/>
        <translate translate-context="collaborators.virtualusers">Reset Passwords</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'

export default {
  name: 'ExportVirtualUsersModal',

  data() {
    return {
      visible: false,
      state: 'initial', // initial, loading
      selectedUsers: [], // Track selected user IDs
      userTableFields: [
        {
          key: 'selected',
          label: '',
          _style: 'width: 40px;'
        },
        {
          key: 'email',
          label: this.$pgettext('collaborators.virtualusers', 'Email')
        },
        {
          key: 'roles',
          label: this.$pgettext('collaborators.virtualusers', 'Roles')
        }
      ]
    }
  },

  computed: {
    ...mapGetters('collaborators', [
      'virtualUsers'
    ]),

    selectedCount() {
      return this.selectedUsers.length
    },

    allSelected() {
      return this.selectedUsers.length === this.virtualUsers.length && this.virtualUsers.length > 0
    }
  },

  methods: {
    ...mapActions('collaborators', [
      'resetAllVirtualUserPasswords'
    ]),

    open(preselectUserId = null) {
      this.state = 'initial'

      // If a specific user ID is provided, select only that user
      // Otherwise select all users by default
      if (preselectUserId) {
        this.selectedUsers = [preselectUserId]
      } else {
        this.selectedUsers = this.virtualUsers.map(u => u.id)
      }

      this.visible = true
    },

    close() {
      this.visible = false
    },

    onHidden() {
      this.state = 'initial'
      this.selectedUsers = []
    },

    toggleUser(userId) {
      const index = this.selectedUsers.indexOf(userId)
      if (index > -1) {
        this.selectedUsers.splice(index, 1)
      } else {
        this.selectedUsers.push(userId)
      }
    },

    handleRowClick(item) {
      this.toggleUser(item.id)
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedUsers = []
      } else {
        this.selectedUsers = this.virtualUsers.map(u => u.id)
      }
    },

    async resetPasswords() {
      // Validate selection
      if (this.selectedUsers.length === 0) {
        await this.notifyError(
            this.$pgettext('collaborators.virtualusers', 'Please select at least one user')
        )
        return
      }

      this.state = 'loading'

      try {
        // Always use the batch endpoint with selected IDs
        const response = await this.resetAllVirtualUserPasswords(this.selectedUsers)

        const credentials = response.credentials || []
        const successCount = response.count || credentials.length
        const errors = response.errors || {}
        const errorCount = Object.keys(errors).length

        // Show error message if some failed
        if (errorCount > 0) {
          await this.notifyError(
              this.$pgettext('collaborators.virtualusers', 'Some passwords could not be reset. {count} succeeded.')
                  .replace('{count}', successCount)
          )
        }

        // Emit credentials to parent to show in VirtualUserCredentialsModal
        if (credentials.length > 0) {
          this.$emit('passwords-reset', credentials)
          await this.notifySuccess(
              this.$pgettext('collaborators.virtualusers', '{count} password(s) reset successfully')
                  .replace('{count}', successCount)
          )
        }

        this.close()
      } catch (error) {
        this.state = 'initial'
        await this.notifyError(
            this.$pgettext('collaborators.virtualusers', 'Failed to reset passwords')
        )
      }
    }
  }
}
</script>

<style scoped>
/* User Selection Table Styles */
.user-selection-table >>> tbody tr {
  cursor: pointer;
}

.user-selection-table >>> tbody tr:hover {
  background-color: #f0f3f5;
}
</style>
